import React, { useState } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { dispatchAPI } from '../../services/api';

/**
 * PRE-DISPATCH QUALITY CHECK (quantities in METERS only)
 *
 * Shared across every screen that renders the dispatch pipeline
 * (Assembly Dashboard, Dispatch Dashboard, Store Dispatch tab) so the
 * gate behaves identically everywhere.
 *
 * Business rule: a dispatch order cannot move to `packing` or `dispatched`
 * until its quality check is `passed` or `partial`. The backend enforces
 * this in dispatchController.updateDispatch — the UI only mirrors it.
 */

// Roles permitted to record the quality check (mirrors backend RBAC in dispatchRoutes.js)
export const QC_ALLOWED_ROLES = [
  'super_admin', 'admin', 'quality_checker',
  'assembly_manager', 'dispatch_manager',
  'supervisor', 'production_manager'
];

export const canRunDispatchQc = (user) =>
  QC_ALLOWED_ROLES.includes(user?.role) ||
  (Array.isArray(user?.permissions) && user.permissions.some(p => QC_ALLOWED_ROLES.includes(p)));

// Total order quantity in meters, summed across all items
export const getOrderMeters = (order) => {
  if (!order || !Array.isArray(order.items)) return 0;
  return order.items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
};

export const QC_STATUS_STYLE = {
  pending: { label: 'QC Pending', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: '⏳' },
  passed: { label: 'QC Passed', color: '#10b981', bg: 'rgba(16,185,129,0.12)', icon: '✔' },
  partial: { label: 'QC Partial', color: '#6366f1', bg: 'rgba(99,102,241,0.12)', icon: '◐' },
  failed: { label: 'QC Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: '✖' }
};

// Is this order cleared to move forward? (keep in sync with backend isQcCleared)
export const isQcCleared = (order) => ['passed', 'partial'].includes(order?.qcStatus);

/** Compact QC status badge with meter breakdown and re-check count. */
export const QcStatusBadge = ({ order, showInspector = false }) => {
  const st = QC_STATUS_STYLE[order?.qcStatus] || QC_STATUS_STYLE.pending;
  const checks = Array.isArray(order?.qcHistory) ? order.qcHistory.length : 0;

  return (
    <div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px',
        borderRadius: '12px', color: st.color, background: st.bg,
        border: `1px solid ${st.color}44`, whiteSpace: 'nowrap'
      }}>
        {st.icon} {st.label}
      </span>

      {(order?.qcPassedMeters > 0 || order?.qcRejectedMeters > 0) && (
        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '3px' }}>
          {order.qcPassedMeters || 0} m passed
          {order.qcRejectedMeters > 0 ? ` / ${order.qcRejectedMeters} m rejected` : ''}
        </div>
      )}

      {checks > 1 && (
        <div style={{ fontSize: '0.68rem', color: '#6366f1', marginTop: '2px' }}>
          {checks} inspections (re-checked)
        </div>
      )}

      {showInspector && order?.qcCheckedByName && (
        <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', marginTop: '2px' }}>
          by {order.qcCheckedByName}
        </div>
      )}
    </div>
  );
};

/** Button that opens the QC modal — label flips to "Re-check" once inspected. */
export const QcActionButton = ({ order, onClick, size = 'sm' }) => {
  const cleared = isQcCleared(order);
  return (
    <button
      type="button"
      className={`btn btn-${size}`}
      onClick={() => onClick(order)}
      title="Mandatory quality check before this order can move to Packing / Dispatch"
      style={{
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        whiteSpace: 'nowrap',
        background: cleared ? 'rgba(16,185,129,0.15)' : '#f59e0b',
        color: cleared ? '#10b981' : '#fff',
        border: cleared ? '1px solid #10b98155' : 'none'
      }}
    >
      🔍 {cleared ? 'Re-check QC' : 'Quality Check'}
    </button>
  );
};

/**
 * The quality check modal. Self-contained: it performs the API call itself
 * and calls onSaved() so the parent can refresh its list.
 */
export const DispatchQcModal = ({ order, onClose, onSaved }) => {
  const orderMeters = getOrderMeters(order);
  const isRecheck = Array.isArray(order?.qcHistory) && order.qcHistory.length > 0;

  const [form, setForm] = useState({
    totalMeters: orderMeters || '',
    passedMeters: '',
    rejectedMeters: '',
    remarks: '',
    inspectionType: isRecheck ? 'final_inspection' : 'first_inspection'
  });
  const [saving, setSaving] = useState(false);

  const preset = (kind) => {
    const total = Number(form.totalMeters) || orderMeters || 0;
    if (kind === 'pass') setForm(p => ({ ...p, passedMeters: total, rejectedMeters: 0 }));
    else if (kind === 'fail') setForm(p => ({ ...p, passedMeters: 0, rejectedMeters: total }));
    else setForm(p => ({ ...p, passedMeters: '', rejectedMeters: '' }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const passed = Number(form.passedMeters) || 0;
    const rejected = Number(form.rejectedMeters) || 0;
    const total = Number(form.totalMeters) || 0;

    if (!form.remarks.trim()) {
      toast.error('Quality check remarks are mandatory.');
      return;
    }
    if (passed + rejected <= 0) {
      toast.error('Enter the passed and/or rejected quantity in meters.');
      return;
    }
    if (total > 0 && passed + rejected > total + 0.001) {
      toast.error(`Inspected meters cannot exceed the order quantity of ${total} m.`);
      return;
    }

    try {
      setSaving(true);
      const res = await dispatchAPI.processQC(order._id, {
        totalMeters: total,
        passedMeters: passed,
        rejectedMeters: rejected,
        remarks: form.remarks.trim(),
        inspectionType: form.inspectionType
      });
      toast.success(res.data?.message || 'Quality check recorded.');
      if (onSaved) onSaved(res.data?.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record quality check');
    } finally {
      setSaving(false);
    }
  };

  const passedNum = Number(form.passedMeters) || 0;
  const rejectedNum = Number(form.rejectedMeters) || 0;

  let outcomePreview = 'Enter the passed / rejected meters to see the outcome.';
  if (passedNum + rejectedNum > 0) {
    if (rejectedNum === 0) outcomePreview = 'Outcome: PASSED — order will be cleared for Box Packing.';
    else if (passedNum === 0) outcomePreview = 'Outcome: FAILED — order will be moved to On Hold until a re-check passes.';
    else outcomePreview = `Outcome: PARTIAL — ${passedNum} m cleared for Packing, ${rejectedNum} m rejected.`;
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 1100, padding: '20px'
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: '100%', maxWidth: '640px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: 'var(--bg-card, var(--bg-secondary))',
        border: '1px solid var(--border)', borderRadius: '16px',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 26px', borderBottom: '1px solid var(--border)', flexShrink: 0
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.12rem', color: 'var(--text-primary)', fontWeight: 800 }}>
              🔍 Pre-Dispatch Quality Check
            </h3>
            <div style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600, marginTop: '2px' }}>
              {order.salesOrderRef || order.dispatchNo || 'Order'} | {order.customerName}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}
          >
            <HiOutlineX />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '22px 26px', overflowY: 'auto', flex: 1 }}>

          <div style={{
            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: '8px', padding: '12px 14px', marginBottom: '20px',
            fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5
          }}>
            <strong style={{ color: '#f59e0b' }}>Quality check is mandatory.</strong> This order cannot move to
            <strong> Box Packing</strong> or <strong>Dispatch</strong> until it is passed.
            All quantities are recorded in <strong>meters</strong>.
          </div>

          {/* Current state + model + order qty */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))',
            gap: '12px', marginBottom: '20px'
          }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>
                Current Status
              </div>
              <QcStatusBadge order={order} />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>
                Model
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {order.items?.[0]?.modelNumber || order.items?.[0]?.description || 'Drag Chain'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>
                Order Quantity
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary)' }}>
                {orderMeters || '--'} meters
              </div>
            </div>
          </div>

          {/* Previous inspections */}
          {isRecheck && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Previous Inspections ({order.qcHistory.length})
              </div>
              <div style={{ maxHeight: '130px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {order.qcHistory.slice().reverse().map((h, idx) => (
                  <div key={idx} style={{
                    fontSize: '0.74rem', color: 'var(--text-secondary)',
                    background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                    borderRadius: '6px', padding: '8px 10px'
                  }}>
                    <strong style={{ color: (QC_STATUS_STYLE[h.outcome] || QC_STATUS_STYLE.pending).color }}>
                      {(h.outcome || '').toUpperCase()}
                    </strong>
                    {` • ${h.passedMeters || 0} m passed / ${h.rejectedMeters || 0} m rejected`}
                    {` • ${h.inspectionType === 'final_inspection' ? 'Re-check' : 'First'}`}
                    {h.checkedByName ? ` • ${h.checkedByName}` : ''}
                    {h.checkedAt ? ` • ${new Date(h.checkedAt).toLocaleString('en-IN')}` : ''}
                    {h.remarks && (
                      <div style={{ color: 'var(--text-dim)', marginTop: '3px' }}>📝 {h.remarks}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick fill */}
          <div style={{ marginBottom: '18px' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Quick Fill
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-sm" onClick={() => preset('pass')}
                style={{ background: '#10b981', color: '#fff', border: 'none', fontWeight: 700 }}>
                ✔ All Pass
              </button>
              <button type="button" className="btn btn-sm" onClick={() => preset('fail')}
                style={{ background: '#ef4444', color: '#fff', border: 'none', fontWeight: 700 }}>
                ✖ All Reject
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => preset('custom')}
                style={{ fontWeight: 700 }}>
                Custom Split
              </button>
            </div>
          </div>

          {/* Meter inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px', marginBottom: '18px' }}>
            <div className="form-group">
              <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                Total Inspected (meters)
              </label>
              <input
                type="number" min="0" step="0.01" className="form-input"
                value={form.totalMeters}
                onChange={e => setForm(p => ({ ...p, totalMeters: e.target.value }))}
                placeholder="e.g. 50"
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 700, display: 'block', marginBottom: '6px' }}>
                Passed (meters) *
              </label>
              <input
                type="number" min="0" step="0.01" className="form-input"
                value={form.passedMeters}
                onChange={e => setForm(p => ({ ...p, passedMeters: e.target.value }))}
                placeholder="0"
                style={{ borderColor: 'rgba(16,185,129,0.4)' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 700, display: 'block', marginBottom: '6px' }}>
                Rejected (meters)
              </label>
              <input
                type="number" min="0" step="0.01" className="form-input"
                value={form.rejectedMeters}
                onChange={e => setForm(p => ({ ...p, rejectedMeters: e.target.value }))}
                placeholder="0"
                style={{ borderColor: 'rgba(239,68,68,0.4)' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                Inspection Type
              </label>
              <select
                className="form-input"
                value={form.inspectionType}
                onChange={e => setForm(p => ({ ...p, inspectionType: e.target.value }))}
              >
                <option value="first_inspection">First Inspection</option>
                <option value="final_inspection">Final / Re-check</option>
              </select>
            </div>
          </div>

          {/* Mandatory remarks */}
          <div className="form-group" style={{ marginBottom: '18px' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
              Inspector Remarks <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              className="form-input"
              rows={3}
              value={form.remarks}
              onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))}
              placeholder="Mandatory: what was checked, dimensions verified, reason for any rejection..."
            />
          </div>

          {/* Outcome preview */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
            borderRadius: '8px', padding: '12px 14px', marginBottom: '20px',
            fontSize: '0.8rem', color: 'var(--text-secondary)'
          }}>
            {outcomePreview}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ fontWeight: 800 }}>
              {saving ? 'Saving...' : 'Save Quality Check'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DispatchQcModal;
